import { GridElement } from '../types';

/**
 * Fusionne les éléments existants avec des coordonnées GPS proches
 * @param elements - Liste des éléments à traiter
 * @param tolerance - Tolérance en degrés pour considérer des coordonnées comme identiques (défaut: 0.0001 = ~11m)
 * @returns Liste des éléments fusionnés
 */
export const mergeElementsWithSameCoordinates = (elements: GridElement[], tolerance: number = 0.0001): GridElement[] => {
  if (elements.length === 0) return elements;

  const mergedElements: GridElement[] = [];
  const processedIndices = new Set<number>();

  console.log(`🔄 Début de la fusion des éléments: ${elements.length} éléments à traiter`);

  elements.forEach((element, index) => {
    if (processedIndices.has(index)) {
      return; // Élément déjà traité
    }

    if (!element.gpsCoordinates) {
      // Élément sans coordonnées GPS, l'ajouter tel quel
      mergedElements.push(element);
      return;
    }

    const lat = element.gpsCoordinates.latitude;
    const lng = element.gpsCoordinates.longitude;

    // Trouver tous les éléments avec des coordonnées similaires
    const similarElements: { element: GridElement; index: number }[] = [{ element, index }];
    
    elements.forEach((otherElement, otherIndex) => {
      if (otherIndex !== index && !processedIndices.has(otherIndex) && otherElement.gpsCoordinates) {
        const otherLat = otherElement.gpsCoordinates.latitude;
        const otherLng = otherElement.gpsCoordinates.longitude;

        // Vérifier si les coordonnées sont dans la tolérance
        const lngDiff = Math.abs(lng - otherLng);
        const latDiff = Math.abs(lat - otherLat);

        if (lngDiff <= tolerance && latDiff <= tolerance) {
          similarElements.push({ element: otherElement, index: otherIndex });
        }
      }
    });

    // Marquer tous les éléments similaires comme traités
    similarElements.forEach(({ index: similarIndex }) => {
      processedIndices.add(similarIndex);
    });

    if (similarElements.length > 1) {
      console.log(`📍 Fusion de ${similarElements.length} éléments aux coordonnées [${lng}, ${lat}]`);
      
      // Fusionner les propriétés de tous les éléments similaires
      const mergedProperties: Record<string, unknown> = {};
      const mergedNames: string[] = [];
      const mergedTypes: string[] = [];
      let mergedLights: any[] = [];

      similarElements.forEach(({ element: similarElement }) => {
        // Fusionner les noms
        if (similarElement.name) {
          mergedNames.push(similarElement.name);
        }

        // Fusionner les types
        mergedTypes.push(similarElement.type);

        // Fusionner les propriétés
        if (similarElement.properties) {
          Object.entries(similarElement.properties).forEach(([key, value]) => {
            if (!mergedProperties[key]) {
              mergedProperties[key] = value;
            }
          });
        }

        // Fusionner les lumières
        if (similarElement.lights && similarElement.lights.length > 0) {
          mergedLights = [...mergedLights, ...similarElement.lights];
        }
      });

      // Créer un nom fusionné
      if (mergedNames.length > 0) {
        const uniqueNames = Array.from(new Set(mergedNames));
        mergedProperties.name = uniqueNames.join(' + ');
      }

      // Déterminer le type principal (priorité: power-plant > transformer > meter > pole)
      const typePriority = { 'power-plant': 4, 'transformer': 3, 'meter': 2, 'pole': 1 };
      let mainType = 'pole';
      let maxPriority = 0;

      mergedTypes.forEach(type => {
        const priority = typePriority[type as keyof typeof typePriority] || 0;
        if (priority > maxPriority) {
          maxPriority = priority;
          mainType = type;
        }
      });

      // Créer l'élément fusionné
      const mergedElement: GridElement = {
        ...element,
        type: mainType as 'power-plant' | 'pole' | 'meter' | 'transformer',
        name: mergedProperties.name as string || `${mainType} fusionné`,
        properties: {
          ...element.properties,
          ...mergedProperties,
          mergedFrom: similarElements.length // Indiquer combien d'éléments ont été fusionnés
        },
        lights: mainType === 'pole' ? mergedLights : undefined
      };

      mergedElements.push(mergedElement);
    } else {
      // Élément unique, l'ajouter tel quel
      mergedElements.push(element);
    }
  });

  console.log(`✅ Fusion terminée: ${elements.length} éléments → ${mergedElements.length} éléments (${elements.length - mergedElements.length} éléments fusionnés)`);
  
  return mergedElements;
};
